import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import Dashboard from './Dashboard';
import Rotina from './Rotina';
import Protocolos from './Protocolos';
import Agentes from './Agentes';
import Configuracoes from './Configuracoes';
import FAD01 from './FAD01';
import FAD07 from './FAD07';
import FAD07Geral from './FAD07Geral';
import Cobertura from './Cobertura';
import ResumoSemanal from './ResumoSemanal';
import ResumoGeral from './ResumoGeral';
import CalendarioEpidemiologico from './CalendarioEpidemiologico';
import BoletimPonto from './BoletimPonto';
import QuadroSupervisor from './QuadroSupervisor';
import Pendencias from './Pendencias';
import UploadPlanilha from './UploadPlanilha';
import PainelAgente from './PainelAgente';
import PontoEstrategicoPE from './PontoEstrategicoPE';
import FAD07PE from './FAD07PE';
import Laboratorio from './Laboratorio';
import Usuarios from './Usuarios';
import Afastamentos from './Afastamentos';
import PlannerFerias from './PlannerFerias';
import RelatorioMensal from './RelatorioMensal';
import HistoricoQuarteirao from './HistoricoQuarteirao';
import CadastroArea from './CadastroArea';
import FechamentoArea from './FechamentoArea';
import FechamentoCiclo from './FechamentoCiclo';
import Comunicacao from './Comunicacao';
import Arquivos from './Arquivos';
import QuadroPositividade from './QuadroPositividade';
import LiraaCiclo from './LiraaCiclo';
import LiraaLaboratorio from './LiraaLaboratorio';
import Tubitos from './Tubitos';
import LiraaQuadro from './LiraaQuadro';
import Alertas from './Alertas';
import { CircularProgress, Box } from '@mui/material';

const PAGE_TO_PATH: Record<string, string> = {
  dashboard:       '/',
  rotina:          '/rotina',
  protocolos:      '/protocolos',
  fad01:           '/fad01',
  agentes:         '/agentes',
  configuracoes:   '/configuracoes',
  fad07:           '/fad07',
  fad07geral:      '/fad07-geral',
  cobertura:       '/cobertura',
  resumo_semanal:  '/resumo-semanal',
  resumo_geral:    '/resumo-geral',
  boletim_ponto:   '/boletim-ponto',
  areas:           '/areas',
  quarteiraos:          '/quarteiraos',
  quadro_supervisor:    '/quadro-supervisor',
  pendencias:           '/pendencias',
  upload:               '/upload',
  painel_agente:        '/painel-agente',
  ponto_estrategico_pe: '/ponto-estrategico-pe',
  fad07_pe:             '/fad07-pe',
  calendario_epi:       '/calendario-epi',
  usuarios:             '/usuarios',
  afastamentos:         '/afastamentos',
  planner_ferias:       '/planner-ferias',
  relatorio_mensal:     '/relatorio-mensal',
  historico_quarteirao: '/historico-quarteirao',
  cadastro_area:        '/cadastro-area',
  fechamento_area:      '/fechamento-area',
  fechamento_ciclo:     '/fechamento-ciclo',
  comunicacao:          '/comunicacao',
  arquivos:             '/arquivos',
  liraa_ciclo:          '/liraa-ciclo',
  liraa_laboratorio:    '/liraa-laboratorio',
  tubitos:              '/tubitos',
  liraa_quadro:         '/liraa-quadro',
  laboratorio:          '/laboratorio',
  quadro_positividade:  '/quadro-positividade',
};

const PATH_TO_PAGE: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_TO_PATH).map(([k, v]) => [v, k])
);

function pageFromPath(path: string): string {
  return PATH_TO_PAGE[path] || PATH_TO_PAGE[path.replace(/\/$/, '')] || 'dashboard';
}

function AppContent() {
  const { usuario, carregando } = useAuth();
  const [pagina, setPagina] = useState(() => pageFromPath(window.location.pathname));
  const [fad01EditarAoAbrir, setFad01EditarAoAbrir] = useState<number | undefined>(undefined);
  // De onde o FAD-01 foi aberto direto num boletim (Laboratorio ou Quadro de Positividade).
  const [fad01Origem, setFad01Origem] = useState<'laboratorio' | 'quadro_positividade' | 'fad07' | 'fechamento_area' | 'tubitos'>('laboratorio');
  // Tubito do LIRAa aberto a partir do Quadro de Positividade LIRAa.
  const [liraaTubitoId, setLiraaTubitoId] = useState<number | undefined>(undefined);

  const navegar = useCallback((nova: string) => {
    const path = PAGE_TO_PATH[nova] ?? '/';
    window.history.pushState({ pagina: nova }, '', path);
    setPagina(nova);
  }, []);

  const voltar = useCallback(() => {
    navegar('dashboard');
  }, [navegar]);

  useEffect(() => {
    const handler = (e: PopStateEvent) => {
      const pg = e.state?.pagina ?? pageFromPath(window.location.pathname);
      setPagina(pg);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Sync URL when page changes programmatically on initial load
  useEffect(() => {
    const path = PAGE_TO_PATH[pagina] ?? '/';
    if (window.location.pathname !== path) {
      window.history.replaceState({ pagina }, '', path);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (carregando) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1B3A6B, #2D5A9E)' }}>
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    );
  }

  if (!usuario) return <Login />;

  if (pagina === 'rotina') return <Rotina onVoltar={voltar} onNavegar={navegar} />;
  if (pagina === 'protocolos') return <Protocolos onVoltar={() => navegar('rotina')} />;
  if (pagina === 'agentes')        return <Agentes        onVoltar={voltar} />;
  if (pagina === 'fad01')          return <FAD01          onVoltar={() => { if (fad01EditarAoAbrir) { setFad01EditarAoAbrir(undefined); navegar(fad01Origem); } else { voltar(); } }} initialFad01Id={fad01EditarAoAbrir} onVoltarLaboratorio={() => { setFad01EditarAoAbrir(undefined); navegar(fad01Origem); }} />;
  if (pagina === 'fad07')          return <FAD07          onVoltar={voltar} onAbrirFAD01={(id) => { setFad01EditarAoAbrir(id); setFad01Origem('fad07'); setPagina('fad01'); }} />;
  if (pagina === 'fad07geral')     return <FAD07Geral     onVoltar={voltar} />;
  if (pagina === 'cobertura')      return <Cobertura      onVoltar={voltar} />;
  if (pagina === 'resumo_semanal') return <ResumoSemanal  onVoltar={voltar} />;
  if (pagina === 'alertas') return <Alertas onVoltar={voltar} />;
  if (pagina === 'resumo_geral')   return <ResumoGeral    onVoltar={voltar} />;
  if (pagina === 'calendario_epi') return <CalendarioEpidemiologico onVoltar={voltar} />;
  if (pagina === 'boletim_ponto')       return <BoletimPonto       onVoltar={voltar} />;
  if (pagina === 'quadro_supervisor')  return <QuadroSupervisor  onVoltar={voltar} />;
  if (pagina === 'pendencias')         return <Pendencias         onVoltar={voltar} />;
  if (pagina === 'configuracoes')  return <Configuracoes  onVoltar={voltar} section="ciclos" />;
  if (pagina === 'upload')           return <UploadPlanilha   onVoltar={voltar} />;
  if (pagina === 'painel_agente')        return <PainelAgente        onVoltar={voltar} />;
  if (pagina === 'ponto_estrategico_pe') return <PontoEstrategicoPE  onVoltar={voltar} />;
  if (pagina === 'fad07_pe')             return <FAD07PE             onVoltar={voltar} />;
  if (pagina === 'laboratorio')          return <Laboratorio          onVoltar={voltar} onAbrirFAD01={(id) => { setFad01EditarAoAbrir(id); setFad01Origem('laboratorio'); setPagina('fad01'); }} />;
  if (pagina === 'areas')          return <Configuracoes  onVoltar={voltar} section="areas" />;
  if (pagina === 'quarteiraos')    return <Configuracoes  onVoltar={voltar} section="quarteiraos" />;
  if (pagina === 'usuarios')       return <Usuarios       onVoltar={voltar} />;
  if (pagina === 'afastamentos')   return <Afastamentos   onVoltar={voltar} />;
  if (pagina === 'planner_ferias') return <PlannerFerias onVoltar={voltar} />;
  if (pagina === 'relatorio_mensal')    return <RelatorioMensal    onVoltar={voltar} />;
  if (pagina === 'historico_quarteirao') return <HistoricoQuarteirao />;
  if (pagina === 'cadastro_area')       return <CadastroArea onVoltar={voltar} />;
  if (pagina === 'fechamento_area')     return <FechamentoArea onVoltar={voltar} onAbrirFAD01={(id) => { setFad01EditarAoAbrir(id); setFad01Origem('fechamento_area'); setPagina('fad01'); }} />;
  if (pagina === 'fechamento_ciclo')    return <FechamentoCiclo onVoltar={voltar} onNavegar={navegar} />;
  if (pagina === 'comunicacao')         return <Comunicacao onVoltar={voltar} />;
  if (pagina === 'arquivos')            return <Arquivos onVoltar={voltar} />;
  if (pagina === 'quadro_positividade') return <QuadroPositividade onVoltar={voltar} onAbrirFAD01={(id) => { setFad01EditarAoAbrir(id); setFad01Origem('quadro_positividade'); setPagina('fad01'); }} />;
  if (pagina === 'liraa_ciclo')         return <LiraaCiclo onVoltar={voltar} onNavegar={navegar} />;
  if (pagina === 'tubitos')             return <Tubitos onVoltar={voltar} onAbrirBoletim={(id) => { setFad01EditarAoAbrir(id); setFad01Origem('tubitos'); setPagina('fad01'); }} />;
  if (pagina === 'liraa_laboratorio')   return <LiraaLaboratorio onVoltar={voltar} tubitoId={liraaTubitoId} onTubitoAberto={() => setLiraaTubitoId(undefined)} />;
  if (pagina === 'liraa_quadro')        return <LiraaQuadro onVoltar={voltar} onNavegar={navegar} onAbrirTubito={(id) => { setLiraaTubitoId(id); navegar('liraa_laboratorio'); }} />;

  return <Dashboard onNavegar={navegar} />;
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
